package com.sweetlime.bingeamigo.presentation.features.home

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.sweetlime.bingeamigo.model.LibraryEpisode
import com.sweetlime.bingeamigo.model.LibraryTvShow
import com.sweetlime.bingeamigo.model.NextEpisodeCardState
import com.sweetlime.bingeamigo.model.SeasonDetail
import com.sweetlime.bingeamigo.model.TvShowDetail
import com.sweetlime.bingeamigo.model.UserActivity
import com.sweetlime.bingeamigo.repository.AppNotificationsRepository
import com.sweetlime.bingeamigo.repository.AuthRepository
import com.sweetlime.bingeamigo.repository.LibraryRepository
import com.sweetlime.bingeamigo.repository.TmdbRepository
import com.sweetlime.bingeamigo.repository.UserRepository
import com.sweetlime.bingeamigo.AppLifecycle
import com.sweetlime.bingeamigo.state.HomeUiState
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.Job
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.launch

class HomeViewModel(
    private val tmdbRepository: TmdbRepository,
    private val userRepository: UserRepository,
    private val authRepository: AuthRepository,
    private val libraryRepository: LibraryRepository,
    private val notificationsRepository: AppNotificationsRepository,
) : ViewModel() {

    private val _uiState = MutableStateFlow(HomeUiState())
    val uiState: StateFlow<HomeUiState> get() = _uiState.asStateFlow()

    private val showDetailCache = HashMap<Int, TvShowDetail>()
    private val seasonDetailCache = HashMap<String, SeasonDetail>()

    private var loadedUid: String? = null
    private var trackingJob: Job? = null
    private var notificationJob: Job? = null

    init {
        viewModelScope.launch {
            authRepository.authStateChanged().collect { uid ->
                if (uid != null && uid != loadedUid) {
                    loadedUid = uid
                    showDetailCache.clear()
                    seasonDetailCache.clear()
                    loadHomeData()
                    restartTrackingObserver()
                    restartNotificationObserver(uid)
                }
            }
        }
    }

    fun loadHomeData() {
        _uiState.value = _uiState.value.copy(isLoading = true, isActivityLoading = true, error = null)
        viewModelScope.launch {
            try {
                val currentUserId = authRepository.currentUserId()

                // A: kick off profile and activity fetches in parallel
                val profileDeferred = async { currentUserId?.let { userRepository.getUserProfile(it) } }
                val activityDeferred = async { loadFollowingActivity(currentUserId) }

                // unblock the header as soon as the profile arrives
                val profile = profileDeferred.await()
                _uiState.value = _uiState.value.copy(
                    isLoading = false,
                    avatarUrl = profile?.avatarUrl ?: "",
                    username = profile?.username ?: ""
                )

                val activity = activityDeferred.await()

                val movieWatchlistDeferred = async { libraryRepository.getWatchlist().first() }
                val movieWatchedDeferred   = async { libraryRepository.getWatched().first() }
                val tvTrackingDeferred     = async { libraryRepository.getTvShowTracking().first() }
                val tvCompletedDeferred    = async { libraryRepository.getTvShowCompleted().first() }

                val movieLibrary = buildMap {
                    movieWatchlistDeferred.await().forEach { put(it.id, it) }
                    movieWatchedDeferred.await().forEach { put(it.id, it) }
                }
                val tvLibrary = buildMap {
                    tvTrackingDeferred.await().forEach { put(it.id, it) }
                    tvCompletedDeferred.await().forEach { put(it.id, it) }
                }

                _uiState.value = _uiState.value.copy(
                    isActivityLoading = false,
                    followingActivity = activity,
                    userMovieLibrary = movieLibrary,
                    userTvLibrary = tvLibrary
                )
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    isLoading = false,
                    isActivityLoading = false,
                    error = e.message ?: "Unknown error"
                )
            }
        }
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    private fun restartTrackingObserver() {
        trackingJob?.cancel()
        trackingJob = viewModelScope.launch {
            AppLifecycle.isForegrounded
                .flatMapLatest { active ->
                    if (active) libraryRepository.getTvShowTracking()
                    else flow { awaitCancellation() }
                }
                .collect { trackingShows ->
                val top6 = trackingShows
                    .sortedByDescending { it.lastMarkedAt?.seconds ?: it.addedAt?.seconds ?: 0L }
                    .take(6)
                val cards = coroutineScope {
                    top6.map { async { buildNextEpisodeCard(it) } }.awaitAll().filterNotNull()
                }
                _uiState.value = _uiState.value.copy(nextEpisodeCards = cards)
            }
        }
    }

    private suspend fun buildNextEpisodeCard(show: LibraryTvShow): NextEpisodeCardState? {
        val showDetail = showDetailCache.getOrPut(show.id) {
            tmdbRepository.getTvShowDetail(show.id) ?: return null
        }

        val watchedEpisodes = show.episodes.filter { it.watched }
        val lastWatched = watchedEpisodes
            .maxWithOrNull(compareBy({ it.seasonNumber }, { it.episodeNumber }))

        val (targetSeason, targetEpisode) = if (lastWatched == null) {
            Pair(1, 1)
        } else {
            val currentSeasonSummary = showDetail.seasons
                .filter { it.seasonNumber > 0 }
                .firstOrNull { it.seasonNumber == lastWatched.seasonNumber }
            val isFinale = currentSeasonSummary != null &&
                lastWatched.episodeNumber >= currentSeasonSummary.episodeCount
            if (isFinale) {
                Pair(lastWatched.seasonNumber + 1, 1)
            } else {
                Pair(lastWatched.seasonNumber, lastWatched.episodeNumber + 1)
            }
        }

        val targetSeasonExists = showDetail.seasons.any {
            it.seasonNumber == targetSeason && it.seasonNumber > 0
        }
        if (!targetSeasonExists) return null

        val cacheKey = "${show.id}:s$targetSeason"
        val seasonDetail = seasonDetailCache.getOrPut(cacheKey) {
            tmdbRepository.getSeasonDetail(show.id, targetSeason) ?: return null
        }

        val episodeData = seasonDetail.episodes.firstOrNull { it.episodeNumber == targetEpisode }
            ?: return null

        return NextEpisodeCardState(
            tvShow = show.tvShow,
            episodeToMark = LibraryEpisode(
                id = episodeData.id,
                watched = false,
                seasonNumber = targetSeason,
                episodeNumber = targetEpisode
            ),
            episodeName = episodeData.name,
            seasonNumber = targetSeason,
            episodeNumber = targetEpisode,
            totalThisSeason = seasonDetail.episodes.size,
            watchedThisSeason = watchedEpisodes.count { it.seasonNumber == targetSeason },
            totalEpisodesInShow = showDetail.numberOfEpisodes,
            airDate = episodeData.airDate
        )
    }

    fun markNextEpisodeWatched(tvShowId: Int) {
        val cards = _uiState.value.nextEpisodeCards
        val card = cards.firstOrNull { it.tvShow.id == tvShowId } ?: return
        _uiState.value = _uiState.value.copy(
            nextEpisodeCards = cards.map { if (it.tvShow.id == tvShowId) it.copy(isMarking = true) else it }
        )
        viewModelScope.launch {
            try {
                libraryRepository.markEpisodeWatched(
                    tvShow = card.tvShow,
                    episode = card.episodeToMark,
                    totalEpisodesInShow = card.totalEpisodesInShow,
                    episodeName = card.episodeName
                )
                // Flow re-emits from Firestore and rebuilds the cards automatically
            } catch (e: Exception) {
                _uiState.value = _uiState.value.copy(
                    nextEpisodeCards = _uiState.value.nextEpisodeCards.map {
                        if (it.tvShow.id == tvShowId) it.copy(isMarking = false) else it
                    }
                )
            }
        }
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    private fun restartNotificationObserver(uid: String) {
        notificationJob?.cancel()
        notificationJob = viewModelScope.launch {
            AppLifecycle.isForegrounded
                .flatMapLatest { active ->
                    if (active) notificationsRepository.observeUnreadCount(uid)
                    else flow { awaitCancellation() }
                }
                .collect { count ->
                    _uiState.value = _uiState.value.copy(hasUnreadNotifications = count > 0)
                }
        }
    }

    // B: fetch each followed user's activity in parallel
    private suspend fun loadFollowingActivity(currentUserId: String?): List<UserActivity> {
        if (currentUserId == null) return emptyList()
        val following = userRepository.getFollowing(currentUserId)
        return coroutineScope {
            following
                .map { async { userRepository.getUserActivity(it.id) } }
                .awaitAll()
                .flatten()
                .sortedByDescending { it.timestamp }
        }
    }
}